import React, { useEffect, useMemo } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';

function normalizeInitialHtml(text = '', html = '') {
  if (html && html.trim()) return html;
  const lines = (text || '').split('\n').map(l => l || '<br>');
  return lines.length ? `<p>${lines.join('</p><p>')}</p>` : '<p></p>';
}

export default function TiptapTextbox({
  valueText,
  valueHtml,
  onChangeHtml,
  onReady,
  fontFamily,
  fontSize,
  fontColor,
  isBold,
  isItalic,
  isUnderline,
  readOnly = false,
}) {
  const extensions = useMemo(() => ([
    StarterKit.configure({ bulletList: false, orderedList: false }),
    BulletList.configure({ keepMarks: true }),
    OrderedList.configure({ keepMarks: true }),
    ListItem.configure({ nested: true }),
  ]), []);

  const editor = useEditor({
    extensions,
    editable: !readOnly,
    content: normalizeInitialHtml(valueText, valueHtml),
    editorProps: {
      attributes: {
        style: `
          outline: none;
          color: ${fontColor || '#000'};
          font-family: ${fontFamily || 'Arial'}, sans-serif;
          font-size: ${fontSize || 24}px;
          ${isBold ? 'font-weight: bold;' : ''}
          ${isItalic ? 'font-style: italic;' : ''}
          ${isUnderline ? 'text-decoration: underline;' : ''}
          line-height: 1.25;
        `,
        class: 'tiptap-textbox',
      },
      handleKeyDown(view, event) {
        if (!editor) return false;
        if (event.key === 'Tab') {
          event.preventDefault();
          if (event.shiftKey) return editor.chain().focus().liftListItem('listItem').run();
          return editor.chain().focus().sinkListItem('listItem').run();
        }
        if (event.key === 'Enter') {
          const { state } = editor;
          const { $from } = state.selection;
          const parent = $from.node($from.depth);
          const isListItem = parent?.type?.name === 'listItem';
          const isEmpty = isListItem && parent.content.size === 0;
          if (isListItem && isEmpty) {
            if (editor.chain().focus().liftListItem('listItem').run()) return true;
            return editor.chain().focus().clearNodes().setParagraph().run();
          }
          return false;
        }
        return false;
      },
    },
    onUpdate({ editor }) {
      const html = editor.getHTML();
      onChangeHtml?.(html);
    },
  });

  useEffect(() => { if (editor && onReady) onReady(editor); }, [editor, onReady]);

  return <EditorContent editor={editor} />;
}



